import React, { useState, useEffect, useCallback } from "react";
import { useEmailChangeStatus, useCompleteEmailChange } from "../../hooks/user";
import { Box, Grid, Card, CardContent, Typography, Avatar, Chip, Button, useTheme, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress } from "@mui/material";
import { Security as SecurityIcon, Password as PasswordIcon, Email as EmailIcon, Cancel as CancelIcon, CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import ChangePasswordDialog from "./ChangePasswordDialog";
import ChangeEmailDialog from "./ChangeEmailDialog";
import CancelEmailChangeDialog from "./CancelEmailChangeDialog";
import { useError } from "../../error/useError";
import { useAuth } from "../../auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";
import { useUserProfileContext } from "../../contexts/ProfileContext";

const SecurityOverviewSection: React.FC = () => {
  const theme = useTheme();
  const { handleError } = useError();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [openChange, setOpenChange] = useState(false);
  const [openEmail, setOpenEmail] = useState(false);
  const [openCancel, setOpenCancel] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [countdown, setCountdown] = useState<number>(5);

  const { userProfile } = useUserProfileContext();
  const { data: status = null, refetch: refreshStatus } = useEmailChangeStatus();
  const { mutate: completeEmailChange } = useCompleteEmailChange();

  const handleSignIn = useCallback(() => {
    setShowSuccessDialog(false);
    // Tokens already cleared in onSuccess, just navigate
    navigate(LOGIN_ROUTE_PATH, { 
      state: { 
        message: "Email changed successfully. Please sign in with your new email." 
      } 
    });
  }, [navigate]);

  // Countdown timer for auto-redirect
  useEffect(() => {
    if (!showSuccessDialog) {
      setCountdown(5); // Reset countdown when dialog closes
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSignIn();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showSuccessDialog, handleSignIn]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar
          sx={{
            backgroundColor: theme.palette.warning.main + '20',
            color: theme.palette.warning.main}}
        >
          <SecurityIcon />
        </Avatar>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Security
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your account security settings
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Avatar
                  sx={{
                    backgroundColor: theme.palette.warning.main + '15',
                    color: theme.palette.warning.main,
                    width: 36,
                    height: 36}}
                >
                  <PasswordIcon />
                </Avatar>
              </Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Password & sign‑in
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Update your password regularly and keep your account secure.
              </Typography>
              <Button variant="contained" onClick={() => setOpenChange(true)}>
                Change password
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Avatar
                  sx={{
                    backgroundColor: theme.palette.info.main + '15',
                    color: theme.palette.info.main,
                    width: 36,
                    height: 36}}
                >
                  <EmailIcon />
                </Avatar>
                {!status?.hasPendingChange && (
                  <Chip 
                    icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                    label="Verified" 
                    size="small" 
                    color="success" 
                    variant="outlined"
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Email address
                </Typography>
                {status?.hasPendingChange && (
                  <Chip label="Pending Change" color="warning" size="small" />
                )}
              </Box>
              
              {/* Current Email Display */}
              {!status?.hasPendingChange && userProfile?.email && (
                <Box sx={{ 
                  p: 1.5, 
                  mb: 2,
                  backgroundColor: theme.palette.grey[50], 
                  borderRadius: 1.5,
                  border: `1px solid ${theme.palette.divider}`
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, wordBreak: 'break-all' }}>
                    {userProfile.email}
                  </Typography>
                </Box>
              )}
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {status?.hasPendingChange
                  ? `Pending change to ${status.pendingEmail || 'new address'}.`
                  : "Your login email address. Click 'Change email' to update it."}
              </Typography>
              {status?.hasPendingChange && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  <Chip
                    label={status.oldEmailVerified ? "Current email verified" : "Current email pending"}
                    color={status.oldEmailVerified ? "success" : "default"}
                    variant={status.oldEmailVerified ? "filled" : "outlined"}
                    size="small"
                  />
                  <Chip
                    label={status.newEmailVerified ? "New email verified" : "New email pending"}
                    color={status.newEmailVerified ? "success" : "default"}
                    variant={status.newEmailVerified ? "filled" : "outlined"}
                    size="small"
                  />
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={() => setOpenEmail(true)} disabled={!!status?.hasPendingChange}>
                  Change email
                </Button>
                {status?.hasPendingChange && (
                  <>
                    <Button variant="text" color="error" startIcon={<CancelIcon />} onClick={() => setOpenCancel(true)}>
                      Cancel pending change
                    </Button>
                    <Tooltip title={status.canComplete ? "Both emails verified" : "Verify both emails to enable"}>
                      <span>
                        <Button
                          variant="contained"
                          disabled={!status.canComplete}
                          onClick={() => {
                            completeEmailChange(undefined, {
                              onSuccess: async () => {
                                // Immediately clear tokens since backend invalidated them
                                await logout();
                                // Show success dialog with countdown
                                setShowSuccessDialog(true);
                              },
                              onError: (error) => {
                                // Use the backend's user-friendly error message
                                handleError(error);
                              }
                            });
                          }}
                        >
                          Complete email change
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        {/* Future cards: 2FA, Sessions, Login Activity, etc. */}
      </Grid>

      <ChangePasswordDialog open={openChange} onClose={() => setOpenChange(false)} />
      <ChangeEmailDialog open={openEmail} onClose={() => { setOpenEmail(false); refreshStatus(); }} onInitiated={refreshStatus} />
      <CancelEmailChangeDialog open={openCancel} onClose={() => setOpenCancel(false)} onCompleted={refreshStatus} />
      
      {/* Success dialog for email change completion */}
      <Dialog open={showSuccessDialog} onClose={() => {}} disableEscapeKeyDown maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Email Changed Successfully!</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your email has been successfully changed. For security reasons, you need to sign in again with your new email address.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Redirecting to login page in <strong>{countdown}</strong> second{countdown !== 1 ? 's' : ''}...
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={(5 - countdown) / 5 * 100} 
            sx={{ 
              height: 6, 
              borderRadius: 3,
              backgroundColor: theme.palette.grey[200],
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleSignIn}
            fullWidth
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
              }
            }}
          >
            Sign In Now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecurityOverviewSection;


